import type { LucideIcon } from "lucide-react";

export type KpiTone = "neutral" | "primary" | "good" | "warn" | "bad";

export interface Kpi {
  label: string;
  value: string;
  unit?: string;
  hint?: string;
  icon?: LucideIcon;
  tone?: KpiTone;
  /** Die Kennzahl, um die es geht: doppelt breit und größer */
  emphasis?: boolean;
}

/** Ton als Farbe: Kunde (primary), Ampel für gut/Achtung/schlecht, sonst gedeckt. */
const TONE: Record<KpiTone, string> = {
  neutral: "hsl(var(--muted-foreground))",
  primary: "var(--customer-primary, hsl(var(--primary)))",
  good: "#1a9850",
  warn: "#e08a00",
  bad: "#d7191c",
};

/**
 * Kennzahlen als Kacheln: Symbol, kurze Beschriftung, Wert mit kleinerer Einheit, Hinweis darunter, ein farbiger Akzent je
 * Bedeutung. Für die Kopfzeilen der Gebäudereiter (PV-Potenzial, Dekarbonisierung).
 */
export default function KpiTiles({ items }: { items: Kpi[] }) {
  return (
    <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(180px,1fr))]">
      {items.map(({ label, value, unit, hint, icon: Icon, tone = "neutral", emphasis }) => {
        const color = TONE[tone];
        return (
          <div
            key={label}
            className={`relative min-w-0 overflow-hidden rounded-lg border bg-card p-4 ${emphasis ? "sm:col-span-2" : ""}`}
            style={{ backgroundImage: `linear-gradient(135deg, color-mix(in srgb, ${color} 9%, transparent), transparent 60%)` }}
          >
            <span aria-hidden="true" className="absolute inset-y-0 left-0 w-1" style={{ background: color }} />
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {Icon && (
                <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full" style={{ background: `color-mix(in srgb, ${color} 16%, transparent)`, color }}>
                  <Icon aria-hidden="true" className="h-3.5 w-3.5" />
                </span>
              )}
              <span className="truncate">{label}</span>
            </div>
            <div className={`mt-2 flex items-baseline gap-1 font-semibold tabular-nums leading-none text-foreground ${emphasis ? "text-4xl" : "text-2xl"}`}>
              <span>{value}</span>
              {unit && <span className={`font-normal text-muted-foreground ${emphasis ? "text-lg" : "text-sm"}`}>{unit}</span>}
            </div>
            {hint && <div className="mt-1.5 text-xs text-muted-foreground">{hint}</div>}
          </div>
        );
      })}
    </div>
  );
}
